import Decimal from 'decimal.js-light';
import { currenciesApi } from '../api/services/currenciesApi';
import { exchangeRatesApi } from '../api/services/exchangeRatesApi';
import {
  decimalGt,
  divideDecimalStrings,
  formatDecimal,
  normalToScaledInt,
  parseDecimalInput,
  scaledIntToDecimal,
  scaledIntToNormal,
  subtractDecimalStrings,
} from '../utils/decimalValues';

const USD_CODE = 'USD';
const DEFAULT_FUNDING_ASSET_CODE = 'USDT';
const DEFAULT_FUNDING_NETWORK_CODE = 'TRC20';
const COMMISSION_DECIMAL_PLACES = 5;
const BASE_COMMISSION_VALUE = '100000';
const MINIMUM_QUOTE_MAX_REFINEMENTS = 4;
const FUNDING_NETWORK_PRIORITY = {
  TRC20: 0,
  ERC20: 1,
};

function getResponseData(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  return [];
}

function formatNetworkName(network) {
  const normalized = String(network || '').trim().replace(/-/g, '').toUpperCase();

  return normalized.replace(/^([A-Z]+)(20)$/, '$1-$2');
}

function normalizeAssetCode(asset) {
  return String(asset?.asset || '').trim().toUpperCase();
}

function normalizeNetworkCode(asset) {
  return String(asset?.network || asset?.networkId || '')
    .trim()
    .replace(/[^A-Z0-9]/g, '')
    .toUpperCase();
}

function getNetworkPriority(asset) {
  return FUNDING_NETWORK_PRIORITY[normalizeNetworkCode(asset)] ?? 2;
}

export function orderFundingAssets(assets = []) {
  const currencyGroups = new Map();

  assets.forEach((asset, index) => {
    const groupKey = asset?.currencyId || normalizeAssetCode(asset) || `currency-${index}`;

    if (!currencyGroups.has(groupKey)) {
      currencyGroups.set(groupKey, {
        assetCode: normalizeAssetCode(asset),
        firstIndex: index,
        assets: [],
      });
    }

    currencyGroups.get(groupKey).assets.push({ asset, index });
  });

  return Array.from(currencyGroups.values())
    .sort((left, right) => {
      const leftPriority = left.assetCode === DEFAULT_FUNDING_ASSET_CODE ? 0 : 1;
      const rightPriority = right.assetCode === DEFAULT_FUNDING_ASSET_CODE ? 0 : 1;

      return leftPriority - rightPriority || left.firstIndex - right.firstIndex;
    })
    .flatMap((group) => (
      group.assets
        .sort((left, right) => (
          getNetworkPriority(left.asset) - getNetworkPriority(right.asset)
          || left.index - right.index
        ))
        .map(({ asset }) => asset)
    ));
}

export function getDefaultFundingAsset(assets = []) {
  const orderedAssets = orderFundingAssets(assets);

  return orderedAssets.find((asset) => (
    normalizeAssetCode(asset) === DEFAULT_FUNDING_ASSET_CODE
    && normalizeNetworkCode(asset) === DEFAULT_FUNDING_NETWORK_CODE
  )) || orderedAssets.find((asset) => (
    normalizeAssetCode(asset) === DEFAULT_FUNDING_ASSET_CODE
  )) || orderedAssets[0] || null;
}

function normalizeDecimalValue(value, decimalPlaces = 0) {
  const normalized = scaledIntToNormal(value, decimalPlaces, { fixed: false, trimZeros: true });

  return parseDecimalInput(normalized) ? normalized : null;
}

function normalizeRate(rate) {
  const value = normalizeDecimalValue(rate?.value, rate?.decimal_places);

  return value && decimalGt(value, '0') ? value : '1';
}

function normalizeCommissionFeeRate(rate) {
  const feeRate = scaledIntToDecimal(
    rate?.commission ?? BASE_COMMISSION_VALUE,
    COMMISSION_DECIMAL_PLACES,
  ).minus(1);

  return feeRate.gt(0) ? feeRate.toString() : '0';
}

function getUsdRateForCurrency(rates, currencyId) {
  const candidates = rates?.[currencyId] || rates?.[`${currencyId}_`] || [];

  return candidates.find((rate) => (
    rate?.is_allowed !== false
    && rate?.currency?.code === USD_CODE
  ));
}

function getDepositNetworks(currency) {
  const totalNetworks = currency?.networks?.total;

  if (!totalNetworks || typeof totalNetworks !== 'object') {
    return [];
  }

  return Object.keys(totalNetworks);
}

export function getFundingAssetKey(asset) {
  return `${asset?.currencyId || ''}:${asset?.networkId || asset?.network || ''}`;
}

export async function loadFundingAssets() {
  const [currenciesResponse, ratesResponse] = await Promise.all([
    currenciesApi.getAll(),
    exchangeRatesApi.getAll(),
  ]);
  const currencies = getResponseData(currenciesResponse);

  const fundingAssets = currencies.flatMap((currency) => {
    const rate = getUsdRateForCurrency(ratesResponse, currency._id);

    if (!rate) {
      return [];
    }

    const networks = getDepositNetworks(currency);

    if (!networks.length) {
      return [];
    }

    return networks.map((network) => ({
      currencyId: currency._id,
      asset: currency.code,
      name: currency.name,
      nameTranslations: currency.name_translations,
      networkId: network,
      network: formatNetworkName(network),
      rateToUsd: normalizeRate(rate),
      commissionFeeRate: normalizeCommissionFeeRate(rate),
      dependsOnAmount: Boolean(rate.depends_on_amount),
      commissionOverride: rate.commission_override_obj,
      outcomeCurrencyId: rate.currency?._id,
      outcomeAsset: rate.currency?.code || USD_CODE,
      outcomeDecimalPlaces: rate.currency?.decimal_places ?? 2,
      iconUrl: currency.file?.urls?.orig,
      iconFilePath: currency.file?.keys?.orig,
      decimalPlaces: currency.decimal_places,
    }));
  });

  return orderFundingAssets(fundingAssets);
}

export function calculateRequiredUsdTopUpForCardBalance(balanceAmountUsd, fundingAsset) {
  const normalizedAmount = parseDecimalInput(balanceAmountUsd);
  const feeRate = parseDecimalInput(fundingAsset?.commissionFeeRate || '0');

  if (!normalizedAmount || normalizedAmount.lte(0)) {
    return '0';
  }

  if (!feeRate || feeRate.lte(0)) {
    return normalizedAmount.toString();
  }

  const netRate = subtractDecimalStrings('1', feeRate.toString());

  return decimalGt(netRate, '0')
    ? divideDecimalStrings(normalizedAmount.toString(), netRate)
    : normalizedAmount.toString();
}

function getDecimalPlaces(value, fallback = 0) {
  const decimalPlaces = Number.parseInt(value, 10);

  return Number.isFinite(decimalPlaces) && decimalPlaces >= 0 ? decimalPlaces : fallback;
}

function getPositiveAmount(value, label) {
  const amount = parseDecimalInput(value);

  if (!amount || amount.lte(0)) {
    throw new Error(`${label} must be greater than zero.`);
  }

  return amount;
}

function getQuoteCurrencyConfig(fundingAsset) {
  const currencyId = fundingAsset?.currencyId;
  const outcomeCurrencyId = fundingAsset?.outcomeCurrencyId;

  if (!currencyId || !outcomeCurrencyId) {
    throw new Error('Funding currency pair is unavailable.');
  }

  return {
    currencyId,
    outcomeCurrencyId,
    fundingDecimalPlaces: getDecimalPlaces(fundingAsset?.decimalPlaces, 2),
    outcomeDecimalPlaces: getDecimalPlaces(fundingAsset?.outcomeDecimalPlaces, 2),
  };
}

function getCalculatedAmount(response, decimalPlaces) {
  const rawAmount = response?.amount;

  if (rawAmount === null || rawAmount === undefined || rawAmount === '') {
    throw new Error('The rate calculation returned no amount.');
  }

  const amount = scaledIntToDecimal(rawAmount, decimalPlaces);

  if (amount.lt(0)) {
    throw new Error('The rate calculation returned an invalid amount.');
  }

  return amount;
}

function buildFundingQuote({ fundingAmount, response, fundingDecimalPlaces, outcomeDecimalPlaces }) {
  const balanceAmount = getCalculatedAmount(response, outcomeDecimalPlaces);
  const feeAmount = response?.fee === null || response?.fee === undefined || response?.fee === ''
    ? new Decimal(0)
    : scaledIntToDecimal(response.fee, fundingDecimalPlaces);
  const normalizedFeeAmount = feeAmount.lt(0) ? new Decimal(0) : feeAmount;
  const feePercent = fundingAmount.gt(0)
    ? normalizedFeeAmount.div(fundingAmount).mul(100)
    : new Decimal(0);

  return {
    fundingAmount: fundingAmount.toString(),
    balanceAmount: balanceAmount.toString(),
    feeAmount: normalizedFeeAmount.toString(),
    feePercent: feePercent.toString(),
    rate: response?.original_rate || response?.rate || null,
  };
}

export async function calculateFundingQuote(amount, fundingAsset) {
  const fundingAmount = getPositiveAmount(amount, 'Funding amount');
  const {
    currencyId,
    outcomeCurrencyId,
    fundingDecimalPlaces,
    outcomeDecimalPlaces,
  } = getQuoteCurrencyConfig(fundingAsset);
  const amountScaled = normalToScaledInt(fundingAmount, fundingDecimalPlaces);
  const response = await exchangeRatesApi.calculateByAmount({
    currencyId,
    newCurrencyId: outcomeCurrencyId,
    amount: amountScaled,
    direction: true,
  });

  return buildFundingQuote({
    fundingAmount,
    response,
    fundingDecimalPlaces,
    outcomeDecimalPlaces,
  });
}

export async function calculateMinimumFundingQuote(balanceAmount, fundingAsset) {
  const requestedBalanceAmount = getPositiveAmount(balanceAmount, 'Balance amount');
  const {
    currencyId,
    outcomeCurrencyId,
    fundingDecimalPlaces,
    outcomeDecimalPlaces,
  } = getQuoteCurrencyConfig(fundingAsset);
  const requestedBalanceScaled = normalToScaledInt(
    requestedBalanceAmount,
    outcomeDecimalPlaces,
    Decimal.ROUND_CEIL,
  );
  const reverseResponse = await exchangeRatesApi.calculateByAmount({
    currencyId,
    newCurrencyId: outcomeCurrencyId,
    amount: requestedBalanceScaled,
    direction: false,
  });
  const reverseFundingAmount = getCalculatedAmount(reverseResponse, fundingDecimalPlaces);
  let roundedFundingAmount = reverseFundingAmount.toDecimalPlaces(0, Decimal.ROUND_CEIL);
  let quote = null;

  if (roundedFundingAmount.lte(0)) {
    throw new Error('The minimum funding amount could not be calculated.');
  }

  for (let attempt = 0; attempt < MINIMUM_QUOTE_MAX_REFINEMENTS; attempt += 1) {
    quote = await calculateFundingQuote(roundedFundingAmount.toString(), fundingAsset);

    const calculatedBalanceAmount = parseDecimalInput(quote.balanceAmount);

    if (calculatedBalanceAmount?.gte(requestedBalanceAmount)) {
      return {
        ...quote,
        requestedBalanceAmount: requestedBalanceAmount.toString(),
      };
    }

    if (!calculatedBalanceAmount || calculatedBalanceAmount.lte(0)) {
      break;
    }

    const proportionalAmount = roundedFundingAmount
      .mul(requestedBalanceAmount)
      .div(calculatedBalanceAmount)
      .toDecimalPlaces(0, Decimal.ROUND_CEIL);

    roundedFundingAmount = proportionalAmount.gt(roundedFundingAmount)
      ? proportionalAmount
      : roundedFundingAmount.plus(1);
  }

  throw new Error('The minimum funding amount could not be satisfied by the calculated rate.');
}

function ceilDecimalToWhole(value) {
  const decimal = parseDecimalInput(value);

  if (!decimal || decimal.lte(0)) {
    return null;
  }

  return decimal.toDecimalPlaces(0, Decimal.ROUND_CEIL);
}

function formatQuickAmount(value, decimalPlaces = 2) {
  const decimal = parseDecimalInput(value);
  const places = Number.parseInt(decimalPlaces, 10);
  const safePlaces = Number.isFinite(places) && places >= 0 ? places : 2;

  if (!decimal || decimal.lte(0)) {
    return '0';
  }

  return formatDecimal(decimal, safePlaces, {
    trimZeros: true,
    minFractionDigits: 0,
  });
}

export function buildQuickTopUpAmounts(minimumAmount) {
  const minimum = ceilDecimalToWhole(minimumAmount);

  if (!minimum) {
    return [];
  }

  const second = minimum.mul(2).div(10).toDecimalPlaces(0, Decimal.ROUND_CEIL).mul(10);
  const third = second.mul(2);

  return Array.from(new Set([
    minimum,
    second,
    third,
  ].map((value) => formatQuickAmount(value, 0))));
}

export function formatFundingQuoteCommissionPercent(quote) {
  const percent = parseDecimalInput(quote?.feePercent || '0');
  const displayPercent = percent && percent.gt(0) ? percent : '0';

  return formatDecimal(displayPercent, 3, {
    trimZeros: true,
    suffix: '%',
  });
}
